import { makeAutoObservable, reaction, runInAction } from 'mobx'

import UserStore from './UserStore'
import {
  type Unit,
  type PlantType,
  type BiskoSector,
  type Carriers,
  type ResidentialType,
  type BuildingRole,
  type MinorRole,
  type ProjectResponse,
  type Supply,
  type JSONObject,
  type ProjectHistoryObject,
  type CustomProjectWMSLayer,
  type BackendAppConfig,
  type FeatureFlags
} from './@types'
import {
  type Quarter,
  FeatureType,
  Project,
  UserRole,
  type Features,
  Aggregation,
  Filter
} from './features'
import MonitorStore from './MonitorStore'
import LayerStore from './LayerStore'
import ViewStore from './ViewStore'
import FilterStore from './FilterStore'
import MapStore from './MapStore'
import { RouterStore } from 'mobx-react-router'
import axios, { type AxiosInstance, type AxiosResponse } from 'axios'
import DashboardStore from './DashboardStore'
import { type AlertColor } from '@mui/material'
import { CachedClient, setFromJSON } from 'helpers'
import { Vegetation, VegetationCollection } from './features/Vegetations'
import { Building, BuildingCollection } from './features/Buildings'
import { EnergyPlant, EnergyPlantCollection } from './features/EnergyPlants'

const NO_HEAT_SUPPLY: Supply = {
  id: 0,
  title: 'Nicht Wärmeversorgt',
  description: '',
  type: null,
  units: [
    {
      id: 1,
      title: 'Kilowattstunde',
      description: '',
      symbol: 'kWh/a'
    }
  ]
}

const ROLES_REQUIRED_FOR_PROJECT_SETTINGS = [UserRole.Editor, UserRole.Manager]

class AppStore {
  public remoteVersion: string | undefined = undefined

  private readonly cached: CachedClient

  public storesInitialized: boolean = false

  public userStore: UserStore
  public monitorStore!: MonitorStore
  public viewStore!: ViewStore
  public mapStore!: MapStore
  public layerStore!: LayerStore
  public filterStore!: FilterStore
  public routingStore: RouterStore
  public dashboardStore!: DashboardStore

  public buildingVersion = new Date().getTime()
  public vegetationVersion = new Date().getTime()
  public energyPlantVersion = new Date().getTime()
  public quarterVersion = new Date().getTime()

  public apiResponse: AxiosResponse | null = null

  private selectedFeatureFocus: Features | null = null

  public projects = new Map<string, Project>()
  public selectedProject!: Project

  public currentHistoryObject: ProjectHistoryObject | null = null
  public fromHistorySelection = false

  public globalFeatureFlags: FeatureFlags | undefined

  constructor() {
    makeAutoObservable(this)

    this.routingStore = new RouterStore()
    this.userStore = new UserStore(this)
    this.cached = new CachedClient(this)

    this.fetchRemoteSystemVersion()
    this.fetchGlobalFeatureFlags()

    // the api response altert will disappear after 2 seconds
    reaction(
      () => this.apiResponse,
      () => {
        this.apiResponse = null
      },
      { delay: 2000 }
    )

    reaction(
      () => this.projects,
      () => {
        // if there is no project selected yet we select the last one
        // if there has not been a project selected we just take the first
        if (this.selectedProject === undefined) {
          const defaultProjectId =
            this.userStore.currentUser?.settings?.selectedProjectId
          const defaultProject: Project =
            this.projects.get(defaultProjectId ?? '') ??
            this.projects.values().next().value
          this.selectProject(defaultProject)
        }
      }
    )

    reaction(
      () => this.selectedProject,
      () => {
        if (this.selectedProject === undefined) return

        this.filterStore = new FilterStore(this)
        this.monitorStore = new MonitorStore(this)
        this.viewStore = new ViewStore(this)
        this.layerStore = new LayerStore(this)
        this.mapStore = new MapStore(this)
        this.dashboardStore = new DashboardStore(this)

        this.storesInitialized = true
      }
    )
  }

  bumpBuildingVersion(): void {
    this.buildingVersion++
  }

  bumpVegetationVersion(): void {
    this.vegetationVersion++
  }

  bumpEnergyPlantVersion(): void {
    this.energyPlantVersion++
  }

  bumpQuarterVersion(): void {
    this.quarterVersion++
  }

  get appStore(): this {
    return this
  }

  get userRole(): UserRole {
    const project: undefined | Project = this.projects.get(
      this.selectedProject.uuid
    )
    return project?.user_role ?? UserRole.Viewer
  }

  fetchGlobalFeatureFlags(): void {
    void axios
      .get<FeatureFlags>(
        `${process.env.REACT_APP_API_URL ?? ''}` + '/api/v1/flags/'
      )
      .then((response) => {
        runInAction(() => (this.globalFeatureFlags = response.data))
      })
  }

  fetchProjects(): void {
    void this.client.get<ProjectResponse[]>('/api/v1/project/').then((res) => {
      runInAction(() => {
        const newProjects = new Map()
        // iterate of the projects response
        for (const row of res.data) {
          const { scenarios, ...projectData } = row
          const project: Project = new Project(this, projectData.uuid)
          setFromJSON(project, projectData, ['uuid'])
          newProjects.set(project.uuid, project)
          for (const scenarioData of scenarios) {
            if (scenarioData.all_simulations_updated) {
              const scenario: Project = new Project(this, scenarioData.uuid)
              setFromJSON(scenario, scenarioData, ['uuid'])
              newProjects.set(scenario.uuid, scenario)
            }
          }
        }
        this.projects = newProjects
      })
    })
  }

  selectProject(project: Project): void {
    this.selectedProject = project
    this.userStore.currentUser?.updateCurrentProject(this.selectedProject)
  }

  set featureFocus(feature: Features | null) {
    this.selectedFeatureFocus = feature
  }

  get featureFocus(): Features {
    return (
      this.selectedFeatureFocus ??
      (this.filterStore.hasBuildingFiltersSelected()
        ? new Filter(this.selectedProject)
        : this.selectedProject)
    )
  }

  focusFilter(): void {
    if (!this.filterStore.hasBuildingFiltersSelected()) this.clearFocus()
    else this.featureFocus = new Filter(this.selectedProject)
  }

  get focusType(): FeatureType {
    return this.featureFocus.type
  }

  get isFocusCollection(): boolean {
    return 'uuids' in this.featureFocus
  }

  get focusIds(): string[] {
    if ('uuids' in this.featureFocus) return this.featureFocus.uuids
    if ('uuid' in this.featureFocus) return [this.featureFocus.uuid]
    return []
  }

  get featureFlags(): Set<string> {
    return new Set([
      ...(this.globalFeatureFlags?.flags ?? []),
      ...(this.selectedProject?.projectFeatureFlags?.flags ?? []),
      ...(this.userFeatureFlags?.flags ?? [])
    ])
  }

  get featureSwitches(): Set<string> {
    return new Set([
      ...(this.globalFeatureFlags?.switches ?? []),
      ...(this.selectedProject?.projectFeatureFlags?.switches ?? []),
      ...(this.userFeatureFlags?.switches ?? [])
    ])
  }

  get featureSamples(): Set<string> {
    return new Set([
      ...(this.globalFeatureFlags?.samples ?? []),
      ...(this.selectedProject?.projectFeatureFlags?.samples ?? []),
      ...(this.userFeatureFlags?.samples ?? [])
    ])
  }

  focus(type: FeatureType, uuid: string): void {
    if (this.selectedProject === null) return

    if (
      this.featureFocus !== null &&
      'uuid' in this.featureFocus &&
      this.featureFocus.uuid === uuid
    )
      this.clearFocus()
    else {
      switch (type) {
        case FeatureType.Quarter:
          this.featureFocus = this.quarters.get(uuid) ?? null
          break
        case FeatureType.Building:
          this.featureFocus = new Building(this.selectedProject, uuid)
          break
        case FeatureType.Vegetation:
          this.featureFocus = new Vegetation(this.selectedProject, uuid)
          break
        case FeatureType.EnergyPlant:
          this.featureFocus = new EnergyPlant(this.selectedProject, uuid)
          break
        default:
          this.clearFocus()
      }
    }
  }

  focusAggregation(uuid: string, properties: JSONObject): void {
    if (this.selectedProject === null) return

    this.featureFocus = new Aggregation(this.selectedProject, uuid, properties)
  }

  focusCollection(type: FeatureType, uuids: string[]): void {
    if (uuids.length === 0) {
      this.clearFocus()
      return
    }
    if (uuids.length === 1) {
      this.focus(type, uuids[0])
      return
    }
    switch (type) {
      case FeatureType.Building:
        this.featureFocus = new BuildingCollection(this.selectedProject, uuids)
        break
      case FeatureType.Vegetation:
        this.featureFocus = new VegetationCollection(
          this.selectedProject,
          uuids
        )
        break
      case FeatureType.EnergyPlant:
        this.featureFocus = new EnergyPlantCollection(
          this.selectedProject,
          uuids
        )
        break
      default:
        this.clearFocus()
    }
  }

  clearFocus(): void {
    this.featureFocus = null
  }

  isFeatureInFocus(type: FeatureType, uuid: string): boolean {
    if (this.focusType !== type) return false
    if ('uuid' in this.featureFocus) return this.featureFocus.uuid === uuid
    if ('uuids' in this.featureFocus)
      return this.featureFocus.uuids.includes(uuid)
    return false
  }

  get quarters(): Map<string, Quarter> {
    return this.selectedProject.quarters ?? new Map()
  }

  get wmsLayerList(): CustomProjectWMSLayer[] {
    return this.selectedProject.userSettings.wmsLayerList ?? []
  }

  set wmsLayerList(layers: CustomProjectWMSLayer[]) {
    void this.selectedProject.updateWMSLayerList(layers)
  }

  getCarrierUnits(carrierId: number): Unit[] | undefined {
    return this.energyCarriers?.get(carrierId)?.units
  }

  fetchRemoteSystemVersion(): void {
    /*
    Get current backend version from API
    Does not neeed to be authenticated
    */
    void axios
      .get(`${process.env.REACT_APP_API_URL ?? ''}` + '/api/v1/system/version/')
      .then((response) => {
        runInAction(() => (this.remoteVersion = response.data))
      })
  }

  apiToMap<T extends { id: number }>(
    endpoint: string
  ): Map<number, T> | undefined {
    return this.cached.get<T[], Map<number, T>>(
      endpoint,
      undefined,
      (data) => new Map(data.map((row) => [row.id, row]))
    )
  }

  get appConfig(): BackendAppConfig | undefined {
    return this.cached.get('/api/eep/configuration/')
  }

  get plantTypes(): Map<number, PlantType> | undefined {
    return this.apiToMap('/api/v1/energyplanttype/')
  }

  get biskoSectors(): Map<number, BiskoSector> | undefined {
    return this.apiToMap('/api/v1/biskosector/')
  }

  get userFeatureFlags(): FeatureFlags | undefined {
    return this.cached.get('/api/v1/user/flags/')
  }

  getBiskoTitle(id: number | undefined): string | undefined {
    return id === undefined ? undefined : this.biskoSectors?.get(id)?.title
  }

  get energyCarriers(): Map<number, Carriers> | undefined {
    return this.apiToMap('/api/v1/energycarrier/')
  }

  get residentalTypes(): Map<number, ResidentialType> | undefined {
    return this.apiToMap('/api/v1/residentialtype/')
  }

  get buildingRoles(): Map<number, BuildingRole> | undefined {
    return this.apiToMap('/api/v1/buildingrole/')
  }

  get minorRoles(): Map<number, MinorRole> | undefined {
    return this.apiToMap('/api/v1/minorrole/')
  }

  get heatSupplies(): Map<number, Supply> | undefined {
    const supplies = this.apiToMap<Supply>('/api/v1/heatsupply/')
    if (supplies === undefined) return
    supplies.set(0, NO_HEAT_SUPPLY)
    return supplies
  }

  get heatSuppliesList(): Supply[] {
    return Array.from(this.heatSupplies?.values() ?? [])
  }

  get powerSupplies(): Map<number, Supply> | undefined {
    return this.apiToMap('/api/v1/powersupply/')
  }

  get powerSuppliesList(): Supply[] {
    return Array.from(this.powerSupplies?.values() ?? [])
  }

  get client(): AxiosInstance {
    return this.userStore.apiClient
  }

  showResponseAlert(response: AxiosResponse): void {
    this.apiResponse = response
  }

  get responseAlertColor(): AlertColor | undefined {
    if (this.apiResponse === null) return
    if (this.apiResponse.status >= 400) return 'error'
    return 'success'
  }

  get responseAlertMessage(): string {
    if (this.apiResponse === null) return ''
    switch (this.apiResponse.status) {
      case 200:
        return 'Daten erfolgreich aktualisiert.'
      case 201:
        return 'Erfolgreich erstellt.'
      default:
        return `Es ist etwas schiefgelaufen (Status Code ${this.apiResponse.status}). ${this.apiResponse.status}.`
    }
  }

  get projectHistory(): ProjectHistoryObject[] {
    return this.selectedProject.userSettings.projectHistory ?? []
  }

  set projectHistory(history: ProjectHistoryObject[]) {
    void this.selectedProject.updateProjectHistory(history)
  }

  setCurrentHistoryProject(
    currentHistoryObject: ProjectHistoryObject | null
  ): void {
    this.currentHistoryObject = currentHistoryObject
  }

  setCurrentHistoryProjectTitle(title: string): void {
    if (this.currentHistoryObject !== null)
      this.currentHistoryObject.title = title
  }

  setCurrentHistoryProjectStatus(newStatus: boolean): void {
    if (this.currentHistoryObject !== null)
      this.currentHistoryObject.new = newStatus
  }

  setFromHistorySelection(fromHistorySelection: boolean): void {
    this.fromHistorySelection = fromHistorySelection
  }

  setHistoryObjectTitle(title: string): void {
    if (this.currentHistoryObject !== null)
      this.currentHistoryObject.title = title
  }

  handleProjectHistory(): void {
    if (this.currentHistoryObject !== null) {
      this.currentHistoryObject.timestamp = new Date().getTime()
    }

    if (
      this.fromHistorySelection &&
      this.currentHistoryObject !== null &&
      !this.currentHistoryObject?.new &&
      this.currentHistoryObject?.new !== undefined
    ) {
      this.projectHistory.sort(
        (a, b) => (a?.timestamp ?? 0) - (b?.timestamp ?? 0)
      )
    }
    if (this.projectHistory === undefined) this.projectHistory = []
    if (
      (this.currentHistoryObject?.new ??
        this.currentHistoryObject?.new === undefined) &&
      this.fromHistorySelection
    ) {
      this.projectHistory = this.projectHistory.filter(
        (object) => object !== this.currentHistoryObject
      )
      this.setCurrentHistoryProjectStatus(false)
    }
    if (this.projectHistory.length === 5) {
      this.projectHistory.shift()
    }
    if (this.currentHistoryObject !== null) {
      this.projectHistory = [...this.projectHistory, this.currentHistoryObject]
    }
  }

  removeProject(projectId: string): void {
    this.projects.delete(projectId)
  }

  get userIsReadOnly(): boolean {
    return [UserRole.Viewer, UserRole.Public].includes(this.userRole)
  }

  get userCanCreateItems(): boolean {
    return !this.userIsReadOnly
  }

  get userCanEditProjectSettings(): boolean {
    // see GEOEIS-1556
    return ROLES_REQUIRED_FOR_PROJECT_SETTINGS.includes(this.userRole)
  }

  get userCanAccessReports(): boolean {
    return this.userRole !== UserRole.Public
  }

  get userCanEditMonitorItems(): boolean {
    return !this.userIsReadOnly
  }

  get userCanDeleteItems(): boolean {
    return !this.userIsReadOnly
  }

  get userCanViewBuildings(): boolean {
    return this.userRole !== UserRole.Public
  }

  get userHasAccessToLandingPageUseCaseActions(): boolean {
    return this.userRole !== UserRole.Public
  }

  get userCanUseFilter(): boolean {
    return this.userRole !== UserRole.Public
  }
}

export default AppStore
